import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Plus,
  Trash2,
  Send,
  Sparkles,
} from "lucide-react";
import {
  API,
  GROUP_TEMPLATES,
} from "./shared";

export function GroupsTab({ authHeader }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: "", description: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/groups`, { headers: authHeader() });
      setGroups(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) return;
    await axios.post(`${API}/admin/groups`, newGroup, { headers: authHeader() });
    setNewGroup({ name: "", description: "" });
    await load();
  };

  const createFromTemplate = async (tpl) => {
    const existing = groups.find((g) => g.name === tpl.name);
    if (existing) {
      setSelected(existing);
      return;
    }
    await axios.post(`${API}/admin/groups`, tpl, { headers: authHeader() });
    await load();
  };

  if (selected) {
    return (
      <GroupManage
        group={selected}
        authHeader={authHeader}
        onBack={() => {
          setSelected(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold">
          Community Groups
        </div>
      </div>

      {/* Suggested templates */}
      <div className="rounded-2xl bg-[#EDEDEE] p-5">
        <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-3 flex items-center gap-2">
          <Sparkles size={12} /> One-tap group templates
        </div>
        <div className="flex flex-wrap gap-2">
          {GROUP_TEMPLATES.map((t) => {
            const already = groups.some((g) => g.name === t.name);
            return (
              <button
                key={t.name}
                data-testid={`admin-group-template-${t.name.replace(/\s+/g, '_').slice(0, 20)}`}
                onClick={() => createFromTemplate(t)}
                title={t.description}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-colors ${
                  already
                    ? "bg-[#899480] text-[#F8F7F3]"
                    : "bg-[#F8F7F3] border border-[#BCB3A6]/40 text-[#3F5043] hover:border-[#899480]"
                }`}
              >
                {already ? "✓" : <Plus size={11} />} {t.name}
              </button>
            );
          })}
        </div>
      </div>

      <form
        onSubmit={create}
        data-testid="admin-group-create"
        className="rounded-2xl bg-[#F8F7F3] border border-[#BCB3A6]/40 p-5 flex flex-col sm:flex-row gap-3"
      >
        <input
          data-testid="admin-group-name"
          required
          placeholder="Group name (e.g. 'IVF Cycle 1 Support')"
          value={newGroup.name}
          onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
          className="flex-1 bg-[#EDEDEE] rounded-xl px-4 py-2.5 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
        />
        <input
          data-testid="admin-group-desc"
          placeholder="Short description (optional)"
          value={newGroup.description}
          onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
          className="flex-[2] bg-[#EDEDEE] rounded-xl px-4 py-2.5 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
        />
        <button
          type="submit"
          data-testid="admin-group-create-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#875F45]"
        >
          <Plus size={13} /> Create
        </button>
      </form>

      {loading ? (
        <div className="text-center py-6 text-[#899480]">
          <Loader2 className="animate-spin inline" size={16} /> Loading…
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-2xl p-10 text-center text-[#899480]">
          No groups yet. Create your first group above.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <button
              key={g.id}
              data-testid={`admin-group-${g.id}`}
              onClick={() => setSelected(g)}
              className="text-left p-5 rounded-2xl bg-[#F8F7F3] border border-[#BCB3A6]/40 hover:border-[#899480] transition-colors"
            >
              <div className="font-serif text-xl text-[#3F5043] leading-tight mb-1">
                {g.name}
              </div>
              {g.description && (
                <div className="text-xs text-[#3F5043]/60 mb-3 line-clamp-2">
                  {g.description}
                </div>
              )}
              <div className="flex gap-3 text-[11px] uppercase tracking-[0.18em] text-[#899480] font-semibold">
                <span>{g.member_count} members</span>
                <span>{g.message_count} msgs</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function GroupManage({ group, authHeader, onBack }) {
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [adminMsg, setAdminMsg] = useState("");

  const load = async () => {
    const [m, msg] = await Promise.all([
      axios.get(`${API}/admin/groups/${group.id}/members`, { headers: authHeader() }),
      axios.get(`${API}/admin/groups/${group.id}/messages`, { headers: authHeader() }),
    ]);
    setMembers(m.data);
    setMessages(msg.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMember = async (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    await axios.post(
      `${API}/admin/groups/${group.id}/members`,
      { email },
      { headers: authHeader() }
    );
    setEmailInput("");
    await load();
  };

  const removeMember = async (email) => {
    await axios.delete(`${API}/admin/groups/${group.id}/members/${encodeURIComponent(email)}`, {
      headers: authHeader(),
    });
    await load();
  };

  const postMsg = async (e) => {
    e.preventDefault();
    if (!adminMsg.trim()) return;
    await axios.post(
      `${API}/admin/groups/${group.id}/messages`,
      { content: adminMsg },
      { headers: authHeader() }
    );
    setAdminMsg("");
    await load();
  };

  return (
    <div className="space-y-5" data-testid="admin-group-manage">
      <button
        onClick={onBack}
        data-testid="admin-group-back"
        className="text-xs uppercase tracking-[0.22em] text-[#899480] hover:text-[#3F5043]"
      >
        ← All groups
      </button>
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-1">
          Managing
        </div>
        <h2 className="font-serif text-3xl text-[#3F5043]">{group.name}</h2>
        {group.description && (
          <div className="text-sm text-[#3F5043]/70 mt-1">{group.description}</div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-[#F8F7F3] border border-[#BCB3A6]/40 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-3">
            Members ({members.length})
          </div>
          <form onSubmit={addMember} className="flex gap-2 mb-4">
            <input
              data-testid="admin-group-add-email"
              required
              type="email"
              placeholder="Invite by email…"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="flex-1 bg-[#EDEDEE] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#899480]"
            />
            <button
              data-testid="admin-group-add-btn"
              type="submit"
              className="px-4 py-2 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45]"
            >
              <Plus size={13} />
            </button>
          </form>
          {members.length === 0 ? (
            <div className="text-sm text-[#899480]">No members yet.</div>
          ) : (
            <ul className="space-y-2">
              {members.map((m) => (
                <li
                  key={m.email}
                  className="flex items-center justify-between bg-[#EDEDEE] rounded-xl px-3 py-2 text-sm"
                >
                  <span className="text-[#3F5043] truncate">{m.email}</span>
                  <button
                    onClick={() => removeMember(m.email)}
                    data-testid={`admin-group-remove-${m.email}`}
                    className="text-[#875F45] hover:text-[#3F5043] p-1"
                    aria-label="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl bg-[#F8F7F3] border border-[#BCB3A6]/40 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-3">
            Messages ({messages.length})
          </div>
          <div className="max-h-72 overflow-y-auto space-y-2 mb-4 pr-1">
            {messages.length === 0 ? (
              <div className="text-sm text-[#899480]">No messages yet.</div>
            ) : (
              messages.slice(-50).map((m) => (
                <div
                  key={m.id}
                  className={`text-sm px-3 py-2 rounded-xl ${
                    m.author_role === "admin"
                      ? "bg-[#875F45]/15 border border-[#875F45]/30"
                      : "bg-[#EDEDEE]"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-0.5">
                    {m.author_name}
                  </div>
                  <div className="text-[#3F5043] whitespace-pre-wrap">
                    {m.content}
                  </div>
                </div>
              ))
            )}
          </div>
          <form onSubmit={postMsg} className="flex gap-2">
            <input
              data-testid="admin-group-msg-input"
              placeholder="Post as Soarlife Team…"
              value={adminMsg}
              onChange={(e) => setAdminMsg(e.target.value)}
              className="flex-1 bg-[#EDEDEE] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#899480]"
            />
            <button
              data-testid="admin-group-msg-send"
              type="submit"
              className="px-4 py-2 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45]"
            >
              <Send size={12} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
